import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, errText } from "../lib/api";
import { liveStore } from "../lib/liveStore";
import { liveSocket } from "../context/RealtimeContext";
import { useAuth } from "../context/AuthContext";
import { Avatar, CreatorName, Loading } from "../components/common";
import { TipModal } from "../components/TipModal";
import { Button } from "../components/ui/button";
import { Radio, Send, Coins, Heart, Share2, X, Film } from "lucide-react";
import { toast } from "sonner";
import { LiveKitRoom, VideoConference, useLocalParticipant } from "@livekit/components-react";
import "@livekit/components-styles";

function ScreenPublisher() {
  const { localParticipant } = useLocalParticipant();
  useEffect(() => {
    if (!localParticipant) return;
    localParticipant.setScreenShareEnabled(true, { audio: true }).catch(() => {});
  }, [localParticipant]);
  return null;
}

export default function LiveRoom() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [ls, setLs] = useState(null);
  const [chat, setChat] = useState([]);
  const [msg, setMsg] = useState("");
  const [tipOpen, setTipOpen] = useState(false);
  const [reactions, setReactions] = useState([]);
  const [lk, setLk] = useState(null); // {token,url,host}
  const [viewers, setViewers] = useState(null);
  const vref = useRef(); const chatEnd = useRef(); const wsRef = useRef();

  const isHost = ls && user && ls.creator_id === user.id;

  useEffect(() => {
    let active = true;
    api.get(`/live/${id}`).then(({ data }) => { if (active) { setLs(data); setChat(data.chat || []); setViewers(data.viewer_count); } }).catch(() => {});
    // Try LiveKit
    api.get("/livekit/status").then(({ data }) => {
      if (data.enabled) api.post(`/live/${id}/token`).then(({ data }) => active && setLk(data)).catch(() => {});
    }).catch(() => {});
    // Live WS for chat + viewer count, with poll fallback
    const ws = liveSocket(id, (m) => {
      if (m.event === "chat") setChat((c) => [...c, m.data]);
      else if (m.event === "viewers") setViewers(m.data);
    });
    wsRef.current = ws;
    const poll = setInterval(() => api.get(`/live/${id}`).then(({ data }) => active && setChat(data.chat || [])).catch(() => {}), 6000);
    return () => { active = false; clearInterval(poll); try { ws && ws.close(); } catch {} };
  }, [id]);

  useEffect(() => {
    if (!lk && isHost && liveStore.liveId === id && liveStore.stream && vref.current) { vref.current.srcObject = liveStore.stream; vref.current.play().catch(() => {}); }
  }, [isHost, id, ls, lk]);

  useEffect(() => { chatEnd.current?.scrollIntoView({ behavior: "smooth" }); }, [chat]);

  const send = async () => {
    if (!msg.trim()) return;
    try { await api.post(`/live/${id}/chat`, { text: msg }); setMsg(""); } catch (e) { toast.error(errText(e)); }
  };
  const react = () => { const r = { id: Math.random(), x: 20 + Math.random() * 60 }; setReactions((rs) => [...rs, r]); setTimeout(() => setReactions((rs) => rs.filter((x) => x.id !== r.id)), 2000); };
  const endStream = async (recording_url) => {
    try {
      await api.post(`/live/${id}/end`, recording_url ? { save_to_tv: true, recording_url, title: ls?.title } : {});
      if (liveStore.stream) liveStore.stream.getTracks().forEach((t) => t.stop());
      liveStore.stream = null;
      if (recording_url) toast.success("Replay saved to VStar TV (Live)");
      nav(recording_url ? "/tv" : "/live");
    } catch { toast.error("Error"); }
  };
  const saveRef = useRef();
  const [savingReplay, setSavingReplay] = useState(false);
  const onReplayFile = async (file) => {
    if (!file) return;
    setSavingReplay(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await endStream(data.url);
    } catch (e) { toast.error(errText(e)); setSavingReplay(false); }
  };

  if (!ls) return <Loading />;
  return (
    <div className="px-4 md:px-6 py-5 grid lg:grid-cols-[1fr_360px] gap-5">
      <div>
        {lk ? (
          <div className="rounded-2xl overflow-hidden bg-black" style={{ height: "60vh" }} data-lk-theme="default">
            <LiveKitRoom serverUrl={lk.url} token={lk.token} connect video={lk.host && (ls?.source === "camera")} audio={lk.host} onDisconnected={() => {}}>
              <VideoConference />
              {lk.host && ls?.source && ls.source !== "camera" && <ScreenPublisher />}
            </LiveKitRoom>
          </div>
        ) : (
          <div className="relative rounded-2xl overflow-hidden bg-black aspect-video flex items-center justify-center">
            {isHost ? <video ref={vref} muted playsInline className="w-full h-full object-cover" data-testid="live-video" />
              : <div className="flex flex-col items-center gap-3 text-muted-foreground"><Avatar src={ls.creator?.avatar_url} name={ls.creator?.display_name} size={96} ring /><p className="text-sm">{ls.creator?.display_name} is live</p></div>}
            <span className="absolute top-3 left-3 flex items-center gap-1 text-xs font-bold bg-destructive px-2 py-1 rounded"><span className="w-1.5 h-1.5 rounded-full bg-white live-dot" /> LIVE</span>
            <span className="absolute top-3 right-3 text-xs glass px-2 py-1 rounded">{viewers ?? ls.viewer_count} watching</span>
            {reactions.map((r) => <span key={r.id} className="absolute bottom-10 text-3xl animate-rise" style={{ left: `${r.x}%` }}>❤️</span>)}
            {!isHost && <div className="absolute bottom-3 inset-x-3 flex items-center gap-2 glass rounded-full p-1.5">
              <button onClick={react} className="w-9 h-9 rounded-full surface-2 flex items-center justify-center text-rose-400"><Heart size={18} /></button>
              <button onClick={() => setTipOpen(true)} className="flex-1 btn-gold rounded-full h-9 flex items-center justify-center gap-1.5 text-sm" data-testid="live-tip-btn"><Coins size={16} /> Send a tip</button>
            </div>}
          </div>
        )}
        <div className="flex items-center gap-3 mt-4">
          <Link to={`/${ls.creator?.username}`}><Avatar src={ls.creator?.avatar_url} name={ls.creator?.display_name} size={46} /></Link>
          <div className="min-w-0"><h1 className="font-display font-semibold">{ls.title}</h1><Link to={`/${ls.creator?.username}`} className="text-sm text-muted-foreground hover:text-eblue"><CreatorName user={ls.creator} /></Link></div>
          <div className="ml-auto flex gap-2">
            {lk && <Button onClick={() => setTipOpen(true)} className="btn-gold rounded-full h-9" data-testid="live-tip-btn"><Coins size={15} className="mr-1" />Tip</Button>}
            <Button variant="outline" className="rounded-full border-white/15 bg-white/5 h-9" onClick={() => { navigator.clipboard?.writeText(window.location.href); toast.success("Link copied"); }}><Share2 size={15} /></Button>
            {isHost && <input ref={saveRef} type="file" accept="video/*" hidden onChange={(e) => e.target.files?.[0] && onReplayFile(e.target.files[0])} />}
            {isHost && <Button onClick={() => saveRef.current?.click()} disabled={savingReplay} variant="outline" className="rounded-full h-9 border-white/15 bg-white/5" data-testid="save-replay-btn"><Film size={15} className="mr-1" />{savingReplay ? "Saving…" : "Save to TV"}</Button>}
            {isHost && <Button onClick={() => endStream()} className="rounded-full bg-destructive h-9" data-testid="end-live-btn"><X size={15} className="mr-1" /> End</Button>}
          </div>
        </div>
        {!lk && isHost && <p className="text-xs text-muted-foreground mt-3 surface-2 rounded-lg p-3">You're broadcasting a local preview. Add LiveKit keys (Admin) to enable real multi-viewer broadcasting — chat, tips and viewer state are fully wired.</p>}
      </div>

      <div className="surface rounded-2xl flex flex-col h-[70vh] lg:h-auto">
        <div className="p-3 border-b border-white/8 font-display font-semibold flex items-center gap-2"><Radio size={16} className="text-destructive" /> Live Chat <span className="ml-auto text-xs text-muted-foreground font-normal">{viewers ?? ls.viewer_count} watching</span></div>
        <div className="flex-1 overflow-y-auto p-3 space-y-2.5 hide-scroll">
          {chat.length === 0 && <p className="text-xs text-muted-foreground text-center py-8">Say hello 👋</p>}
          {chat.map((m) => (
            <div key={m.id} className="flex gap-2 text-sm"><Avatar src={m.avatar_url} name={m.display_name} size={26} /><div><span className="text-eblue font-medium text-xs mr-1">{m.display_name}</span><span className="text-white/90">{m.text}</span></div></div>
          ))}
          <div ref={chatEnd} />
        </div>
        <div className="p-3 border-t border-white/8 flex gap-2">
          <input value={msg} onChange={(e) => setMsg(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Chat…" className="flex-1 surface-2 border border-white/10 rounded-full px-4 text-sm outline-none" data-testid="live-chat-input" />
          <button onClick={send} className="w-10 h-10 rounded-full btn-blue flex items-center justify-center shrink-0" data-testid="live-chat-send"><Send size={16} /></button>
        </div>
      </div>
      <TipModal open={tipOpen} onClose={() => setTipOpen(false)} recipient={ls.creator} refType="live" refId={ls.id} />
    </div>
  );
}
